// Auto-derives the AB-tests `extraParams` targeting bag from the current request: `cbscidmt`
// and `promo` from the query string, `flowType` from the pathname. The CMS `promo_code`
// override and Campaign Service promos are resolved elsewhere and merged in by callers.

import { AbTestsExtraParamKey, AbTestsExtraParams } from '@/interfaces/abtests';
import { LOB, LOB_PPLUS } from '@/utils/server/lob-utils';

type QueryValue = string | string[] | undefined;
export type AbTestsQueryLike = Record<string, QueryValue>;

// showId is a manually-supplied targeting param, not derived from the URL.
const QUERY_EXTRA_PARAM_KEYS: readonly Exclude<AbTestsExtraParamKey, 'flowType' | 'showId'>[] = [
    'cbscidmt',
    'promo',
];

function firstValue(value: QueryValue): string | undefined {
    return Array.isArray(value) ? value[0] : value;
}

/**
 * Extracts the AB-test-relevant keys from a query-like object (server `req.url` query
 * string or client `router.query`). Returns `undefined`, not `{}`, when nothing matches.
 */
export function deriveAbTestsExtraParamsFromQuery(
    query: AbTestsQueryLike | undefined,
): AbTestsExtraParams | undefined {
    if (!query) {
        return undefined;
    }

    const derived: AbTestsExtraParams = {};
    for (const key of QUERY_EXTRA_PARAM_KEYS) {
        const value = firstValue(query[key]);
        if (value) {
            derived[key] = value;
        }
    }

    return Object.keys(derived).length > 0 ? derived : undefined;
}

// Upsell only exists on the pplus app-shell — ptv-appshell mounts this same global
// provider, but its own "/" is the Pluto Front Door, a completely different page.
const UPSELL_ROUTES: ReadonlyArray<{
    pathname: string;
    exact?: boolean;
    flowType: string;
}> = [
    { pathname: '/', exact: true, flowType: 'dtc' },
    { pathname: '/account/signup/edu-offer', flowType: 'edu' },
    { pathname: '/account/signup/live-tv', flowType: 'livetv' },
];

// Mirrors buildUpsellRewrites' `/:region` rule: INTL Upsell visitors hit a bare two-letter
// region prefix that rewrites to "/", but req.url/router.asPath still show the original path.
const UPSELL_REGION_ROOT = /^\/(?!tn)[a-zA-Z]{2}\/?$/;

/**
 * Classifies the pathname into `dtc`/`edu`/`livetv`, or `undefined` for anything else.
 * Uses `req.url`/`router.asPath` (the pre-rewrite path), not `router.pathname`, which the
 * app-shell rewrite layer resolves to `/` before this ever sees it. `lob` scopes the Upsell
 * routes to pplus only, since ptv-appshell shares this same global derivation.
 */
export function deriveFlowType(
    pathname: string | undefined,
    lob: LOB,
): AbTestsExtraParams['flowType'] {
    if (!pathname || lob !== LOB_PPLUS) {
        return undefined;
    }

    const path = pathname.split('?')[0];
    if (UPSELL_REGION_ROOT.test(path)) {
        return 'dtc';
    }

    const match = UPSELL_ROUTES.find((route) =>
        route.exact ? path === route.pathname : path.startsWith(route.pathname),
    );

    return match?.flowType;
}

/**
 * Combines the query-derived extraParams (`cbscidmt`/`promo`) with the pathname-derived
 * `flowType` into a single derived bag, still `undefined` (not `{}`) when nothing matched.
 */
export function deriveAbTestsExtraParamsFromRequest(
    query: AbTestsQueryLike | undefined,
    pathname: string | undefined,
    lob: LOB,
): AbTestsExtraParams | undefined {
    const derivedFromQuery = deriveAbTestsExtraParamsFromQuery(query);
    const flowType = deriveFlowType(pathname, lob);

    if (!derivedFromQuery && !flowType) {
        return undefined;
    }

    return { ...derivedFromQuery, ...(flowType && { flowType }) };
}

/**
 * Merges automatically-derived extraParams with a caller-supplied bag, caller-supplied keys
 * winning on collision (e.g. the Upsell page's CMS-sourced `promo` overriding the query-string
 * one). Returns `undefined` when both sides are empty, matching `deriveAbTestsExtraParamsFromQuery`.
 */
export function mergeAbTestsExtraParams(
    derived: AbTestsExtraParams | undefined,
    explicit: AbTestsExtraParams | undefined,
): AbTestsExtraParams | undefined {
    if (!derived && !explicit) {
        return undefined;
    }

    return { ...derived, ...explicit };
}
